/**
 * Wellness Check-In Modal — "moment" format
 * The screen IS the question. No card; a full-bleed calm canvas built around
 * the employee. Serif prompt, circular mood dots, private note, closure.
 */
import { useState } from 'react';
import {
  Dialog,
  Box,
  Button,
  Typography,
  Chip,
  TextField,
  LinearProgress,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
  alpha
} from '@mui/material';
import {
  SentimentVeryDissatisfied,
  SentimentDissatisfied,
  SentimentNeutral,
  SentimentSatisfied,
  SentimentVerySatisfied,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  LockOutlined as LockIcon,
  AutoAwesome as SparkIcon
} from '@mui/icons-material';
import * as wellnessAPI from '../../services/api/wellnessAPI';
import apiClient from '../../services/api/config';
import toast from 'react-hot-toast';

interface WellnessCheckInModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: (suggestions: string[]) => void;
}

// mood colors pull from theme semantics (see DESIGN.md) — resolved at render.
const MOOD_OPTIONS = [
  { value: 'very_negative', label: 'Heavy', icon: SentimentVeryDissatisfied, tone: 'error' as const },
  { value: 'negative', label: 'Meh', icon: SentimentDissatisfied, tone: 'warning' as const },
  { value: 'neutral', label: 'Okay', icon: SentimentNeutral, tone: 'muted' as const },
  { value: 'positive', label: 'Good', icon: SentimentSatisfied, tone: 'primary' as const },
  { value: 'very_positive', label: 'Great', icon: SentimentVerySatisfied, tone: 'success' as const }
];

const STATE_OPTIONS = [
  'Stressed', 'Tired', 'Focused', 'Overwhelmed', 'Motivated',
  'Anxious', 'Calm', 'Energized', 'Frustrated', 'Content',
  'Burned out', 'Inspired', 'Confused', 'Confident', 'Worried'
];

const SERIF = "'Iowan Old Style','Palatino Linotype',Palatino,Georgia,serif";

const DAY_NAMES = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

export default function WellnessCheckInModal({ open, onClose, onComplete }: WellnessCheckInModalProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [step, setStep] = useState(1);
  const [mood, setMood] = useState<string>('');
  const [states, setStates] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [airaResponse, setAiraResponse] = useState<string>('');
  const [copingTechniques, setCopingTechniques] = useState<Array<{ title: string; description: string; action: string }>>([]);
  const [loadingTechniques, setLoadingTechniques] = useState(false);

  const toneColor = (tone: string) => {
    switch (tone) {
      case 'error': return theme.palette.error.main;
      case 'warning': return theme.palette.warning.main;
      case 'success': return theme.palette.success.main;
      case 'primary': return theme.palette.primary.main;
      default: return theme.palette.text.secondary;
    }
  };

  const handleMoodSelect = (value: string) => {
    setMood(value);
    const responses: Record<string, string> = {
      'very_negative': "I'm sorry you're having a really tough day. It's okay to not be okay. Let's find what might help.",
      'negative': "I hear you — today has been challenging. Let's work together to find some support.",
      'neutral': "Thanks for checking in. Even on okay days it's good to take a moment for yourself.",
      'positive': "That's good to hear. Let's build on this and notice what's working well for you.",
      'very_positive': "So glad you're feeling great. Let's capture what's contributing to it."
    };
    setAiraResponse(responses[value] || '');
    generateCopingTechniques(value, states);
  };

  const generateCopingTechniques = async (moodValue: string, currentStates: string[]) => {
    setLoadingTechniques(true);
    try {
      const response = await apiClient.post('/ai/wellness-techniques', { mood: moodValue, states: currentStates });
      if (response.data?.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
        setCopingTechniques(response.data.data);
        return;
      }
    } catch (err) {
      console.error('AI techniques error, using fallbacks:', err);
    } finally {
      setLoadingTechniques(false);
    }
    const fallback: Record<string, Array<{ title: string; description: string; action: string }>> = {
      'very_negative': [
        { title: '5-4-3-2-1 Grounding', description: 'A quick way to feel present when overwhelmed.', action: 'Name 5 things you see, 4 you can touch, 3 you hear, 2 you smell, 1 you taste.' },
        { title: 'Reach out for support', description: 'Talking to someone can make a real difference.', action: 'Reach out to a trusted colleague, friend, or mental-health professional.' },
        { title: 'Take a mental-health break', description: 'Sometimes stepping away is the best thing you can do.', action: 'Take 15–30 minutes away. Walk, listen to music, do something you enjoy.' }
      ],
      'negative': [
        { title: 'Box breathing', description: 'A simple technique to lower stress.', action: 'Breathe in 4, hold 4, exhale 4, hold 4. Repeat four times.' },
        { title: 'Prioritise your tasks', description: 'Feeling overwhelmed? Break it down.', action: 'List your top 3 priorities today. Focus on one at a time.' },
        { title: 'Move your body', description: 'Movement can shift your mental state.', action: 'Take a 10-minute walk or do some stretches.' }
      ],
      'neutral': [
        { title: 'Gratitude practice', description: 'Shift focus to the positive.', action: 'Write down 3 things you are grateful for today.' },
        { title: 'Mindful moment', description: 'Take a pause to be present.', action: 'Spend 5 minutes in quiet reflection.' },
        { title: 'Connect with others', description: 'Social connection boosts wellbeing.', action: 'Have a real conversation with a colleague or friend.' }
      ],
      'positive': [
        { title: 'Capture the moment', description: 'Document what is working well.', action: 'Note what contributed to your mood today.' },
        { title: 'Share the positivity', description: 'Spread the good energy.', action: 'Recognise a colleague or share a win with your team.' },
        { title: 'Build on success', description: 'Use this energy productively.', action: 'Tackle a challenging task while you have momentum.' }
      ],
      'very_positive': [
        { title: 'Celebrate your wins', description: 'Acknowledge your achievements.', action: 'Recognise what you have accomplished and how far you have come.' },
        { title: 'Pay it forward', description: 'Help others feel good too.', action: 'Offer support to a colleague who could use it.' },
        { title: 'Set a new goal', description: 'Channel this energy into growth.', action: 'Pick one actionable thing you want to achieve next.' }
      ]
    };
    setCopingTechniques(fallback[moodValue] || []);
  };

  const handleStateToggle = (state: string) => {
    setStates(prev => prev.includes(state) ? prev.filter(s => s !== state) : [...prev, state]);
  };

  const handleNext = () => {
    if (step === 1 && !mood) {
      toast.error("Pick how you're feeling first");
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => setStep(step - 1);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const answers = [
        { questionId: 'q1', prompt: 'How are you feeling today?', choice: mood, response: MOOD_OPTIONS.find(m => m.value === mood)?.label || mood },
        { questionId: 'q2', prompt: 'What best describes your current state?', choice: states.join(', '), response: states.join(', ') },
        { questionId: 'q3', prompt: "Anything on your mind?", choice: notes, response: notes }
      ];
      const result = await wellnessAPI.submitCheckIn({
        mood,
        answers,
        notes: notes.trim() || undefined,
        tags: states,
        source: 'aira-daily'
      });
      toast.success('Logged — +10 points');
      onComplete(result.suggestions);
      handleClose();
    } catch (error) {
      console.error('Error submitting wellness check-in:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setMood('');
    setStates([]);
    setNotes('');
    setAiraResponse('');
    setCopingTechniques([]);
    onClose();
  };

  const dayName = DAY_NAMES[new Date().getDay()];
  const progress = (step / 2) * 100;
  const eyebrow = step === 1 ? `${dayName} · JUST YOU AND THIS` : 'ONE MORE THING';

  // soft calm wash derived from brand blue + accent purple (no hardcoded brand hex)
  const canvasBg = `linear-gradient(160deg, ${alpha(theme.palette.primary.light, 0.10)} 0%, ${alpha(theme.palette.primary.light, 0.06)} 50%, ${alpha(theme.palette.secondary.light, 0.10)} 100%)`;

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      slotProps={{
        backdrop: { sx: { backgroundColor: alpha(theme.palette.common.black, 0.7), backdropFilter: 'blur(4px)' } }
      }}
      PaperProps={{
        elevation: 24,
        sx: {
          borderRadius: fullScreen ? 0 : 5,
          backgroundImage: canvasBg,
          backgroundColor: theme.palette.background.paper,
          overflow: 'hidden',
          // The body scrolls and the footer stays put; this keeps the old
          // 440px canvas on larger screens without outgrowing a short window.
          minHeight: { sm: 'min(443px, calc(100% - 64px))' }
        }
      }}
      sx={{ zIndex: (t) => t.zIndex.modal + 2 }}
    >
      <LinearProgress variant="determinate" value={progress} sx={{ height: 3, flexShrink: 0 }} />

      {step > 1 && (
        <IconButton
          size="small"
          onClick={handleBack}
          disabled={isSubmitting}
          sx={{ position: 'absolute', top: fullScreen ? 'calc(12px + env(safe-area-inset-top))' : 12, left: 12, zIndex: 1 }}
          aria-label="Back"
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
      )}

      <IconButton
        size="small"
        onClick={handleClose}
        sx={{ position: 'absolute', top: fullScreen ? 'calc(12px + env(safe-area-inset-top))' : 12, right: 12, zIndex: 1 }}
        aria-label="Close"
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          px: { xs: 3, sm: 5 },
          pt: 4,
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto'
        }}
      >
        <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.18em', color: 'text.secondary' }}>
          {eyebrow}
        </Typography>

        <Typography
          component="h2"
          sx={{
            fontFamily: SERIF,
            fontWeight: 600,
            fontSize: 'clamp(26px, 4vw, 38px)',
            letterSpacing: '-0.015em',
            color: 'text.primary',
            mt: 1.2,
            mb: 3
          }}
        >
          {step === 1 ? 'How are you, really?' : "What's it about?"}
        </Typography>

        {step === 1 && (
          <>
            <Stack direction="row" spacing={{ xs: 1, sm: 2 }} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              {MOOD_OPTIONS.map((option) => {
                const Icon = option.icon;
                const c = toneColor(option.tone);
                const selected = mood === option.value;
                return (
                  <Stack key={option.value} alignItems="center" spacing={0.5}>
                    <IconButton
                      onClick={() => handleMoodSelect(option.value)}
                      aria-label={option.label}
                      sx={{
                        width: 68,
                        height: 68,
                        border: `2.5px solid ${c}`,
                        color: selected ? theme.palette.getContrastText(c) : c,
                        backgroundColor: selected ? c : theme.palette.background.paper,
                        transition: 'all .2s ease',
                        transform: selected ? 'scale(1.1)' : 'none',
                        boxShadow: selected ? `0 10px 26px ${alpha(c, 0.4)}` : theme.shadows[1],
                        '&:hover': { backgroundColor: selected ? c : alpha(c, 0.1), transform: 'translateY(-4px) scale(1.05)' }
                      }}
                    >
                      <Icon sx={{ fontSize: 32 }} />
                    </IconButton>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: selected ? c : 'text.secondary' }}>
                      {option.label}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>

            {airaResponse && (
              <Box
                sx={{
                  mt: 3,
                  maxWidth: 460,
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  color: 'text.secondary'
                }}
              >
                <SparkIcon sx={{ fontSize: 18, color: 'secondary.main', mt: '2px' }} />
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{airaResponse}</Typography>
              </Box>
            )}

            {(loadingTechniques || copingTechniques.length > 0) && (
              <Box sx={{ mt: 2.5, width: '100%', maxWidth: 460, textAlign: 'left' }}>
                {loadingTechniques ? (
                  <Typography variant="caption" color="text.secondary">Aria is thinking of something that might help…</Typography>
                ) : (
                  <Stack spacing={1}>
                    {copingTechniques.slice(0, 3).map((t, i) => (
                      <Box
                        key={i}
                        sx={{
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          backgroundColor: alpha(theme.palette.background.paper, 0.7),
                          px: 2,
                          py: 1.25
                        }}
                      >
                        <Typography variant="body2" fontWeight={700}>{t.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{t.action}</Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            )}
          </>
        )}

        {step === 2 && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, maxWidth: 460, mb: 3 }}>
              {STATE_OPTIONS.map((state) => {
                const selected = states.includes(state);
                return (
                  <Chip
                    key={state}
                    label={state}
                    onClick={() => handleStateToggle(state)}
                    clickable
                    sx={{
                      borderRadius: 999,
                      fontWeight: selected ? 700 : 500,
                      border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
                      backgroundColor: selected ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                      color: selected ? 'primary.main' : 'text.primary',
                      '&:hover': { backgroundColor: selected ? alpha(theme.palette.primary.main, 0.18) : theme.palette.action.hover }
                    }}
                  />
                );
              })}
            </Box>

            <TextField
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything on your mind? Optional. Private."
              variant="standard"
              multiline
              maxRows={3}
              sx={{
                width: 'min(440px, 86%)',
                mb: 1,
                '& .MuiInput-input': { textAlign: 'center', fontSize: 14 }
              }}
            />
          </>
        )}
      </Box>

      {/* Footer sits outside the scrolling body so Continue is always on screen. */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          px: { xs: 3, sm: 5 },
          pt: { xs: 2, sm: 3 },
          pb: { xs: 3, sm: 4 }
        }}
      >
        <Button
          variant="contained"
          onClick={step < 2 ? handleNext : handleSubmit}
          disabled={isSubmitting || (step === 1 && !mood)}
          size="large"
          sx={{ borderRadius: 999, px: 4, py: 1.25, fontWeight: 700 }}
        >
          {step < 2 ? 'Continue' : (isSubmitting ? 'Logging…' : 'Log it — +10 points')}
        </Button>

        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mt: 2, color: 'text.secondary' }}>
          <LockIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption">Yours alone. Managers see team weather, never this.</Typography>
        </Box>
      </Box>
    </Dialog>
  );
}
